/**
 * A reusable pagination component, that the user can interact with to navigate
 * to different pages of results in different tables
 */
import { get, noop, omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'tss-react/mui';

import { ReactComponent as NextSVG } from '../../Assets/icons/next.svg';
import { ReactComponent as PrevSVG } from '../../Assets/icons/prev.svg';
import styles from './ReusableComponents.module.css';

const extraStyles = (theme, props) => ({
  arrow: {
    alignItems: 'center',
    backgroundColor: get(theme, 'palette.bg.main'),
    display: 'flex',
    margin: '10px',
    padding: '8px',
    '& svg': {
      opacity: props.disabled ? '0.2' : '1',
    },
    '&:hover': {
      backgroundColor: props.disabled
        ? get(theme, 'palette.bg.main')
        : get(theme, 'palette.bg.dark'),
      cursor: props.disabled ? 'default' : 'pointer',
    },
  },
  inputContainer: {
    backgroundColor: get(theme, 'palette.bg.main'),
  },
  input: {
    border: '0px',
    backgroundColor: get(theme, 'palette.bg.main'),
    fontSize: '12px',
    fontWeight: 'bold',
    height: '26px',
    margin: '0px 10px',
    textAlign: 'center',
    width: '26px',
    '&:focus': {
      outline: '0',
    },
  },
});

const NextIcon = withStyles(
  (props) => (
    <div
      {...omit(props, 'height', 'width', 'classes')}
      className={props.classes.arrow}
    >
      <NextSVG {...omit(props, 'classes')} />
    </div>
  ),
  extraStyles,
);

const PrevIcon = withStyles(
  (props) => (
    <div
      {...omit(props, 'height', 'width', 'classes')}
      className={props.classes.arrow}
    >
      <PrevSVG {...omit(props, 'classes')} />
    </div>
  ),
  extraStyles,
);

function Pagination({ rowsPerPage = 15, count, onChangePage, page }) {
  const [pageValue, setPageValue] = useState(page || 1);

  useEffect(() => {
    setPageValue(page);
  }, [page]);

  /**
   * Decrements the page number to the previous page in the list. Calls the
   * onChangePage function from props and reads the page prop, and subtracts
   * one to the page parameter in the url.
   */
  const handleBackButtonClick = () => {
    const nextPage = page - 1;

    setPageValue(nextPage);
    onChangePage(nextPage);
  };

  /**
   * Increments the page number to the next page in the list. Calls the
   * onChangePage function from props an reads the page number from props and
   * adds one to the page parameter in the url.
   */
  const handleNextButtonClick = () => {
    const nextPage = page + 1;

    setPageValue(nextPage);
    onChangePage(nextPage);
  };

  const lastPage = Math.ceil(count / rowsPerPage);
  const itemStart = rowsPerPage * (pageValue - 1) + 1;
  const itemEnd = rowsPerPage * pageValue;

  return !count ? null : (
    <div className={styles.paginationContainer}>
      <div className={styles.pagination}>
        <PrevIcon
          className="prevPage"
          data-testid="prevPageButton"
          onClick={pageValue !== 1 ? handleBackButtonClick : noop}
          height="12"
          width="12"
          disabled={pageValue === 1}
        />
        <p>
          Showing {count === 1 ? '' : `${itemStart} - `}
          {count < itemEnd ? count : itemEnd} of {count}
        </p>

        <NextIcon
          className="nextPage"
          data-testid="nextPageButton"
          onClick={pageValue !== lastPage ? handleNextButtonClick : noop}
          height="12"
          width="12"
          disabled={pageValue === lastPage}
        />
      </div>
    </div>
  );
}

Pagination.propTypes = {
  rowsPerPage: PropTypes.number,
  count: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
};

export default Pagination;
